/** Shared parent-side classification of booking lifecycle states.
 *  These are UI classifications only - no backend status is changed. */

/** Local calendar date (not UTC) so late-evening IST users aren't a day behind. */
export function todayLocal() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

const TODAY = todayLocal;

export const ACTION_STATUSES = ['pending_parent_confirmation', 'awaiting_feedback'];
export const PENDING_STATUSES = ['requested', 'pending', 'pending_coordinator_action', 'awaiting_reschedule'];
export const CONFIRMED_STATUSES = ['confirmed', 'scheduled'];
export const CLOSED_STATUSES = ['completed', 'cancelled', 'rejected', 'not_conducted'];

export function isUpcoming(b, today = TODAY()) {
  return CONFIRMED_STATUSES.includes(b.status) && (b.assigned_date || '') >= today;
}

export function isActionRequired(b) {
  return ACTION_STATUSES.includes(b.status);
}

export function isPending(b) {
  return PENDING_STATUSES.includes(b.status);
}

/** Past requests and final outcomes: closed experiences, unfulfilled requests and
 *  anything whose scheduled date has passed. Future items awaiting a decision stay
 *  in Action Required / Pending only. */
export function isHistory(b, today = TODAY()) {
  if (CLOSED_STATUSES.includes(b.status) || b.status === 'waitlisted') return true;
  if (isActionRequired(b) || isPending(b) || isUpcoming(b, today)) return false;
  return Boolean(b.assigned_date) && b.assigned_date < today;
}

/** Role-neutral label for shared detail pages. */
export function neutralStatusLabel(status, assignedDate, today = TODAY()) {
  const past = (assignedDate || '') < today;
  switch (status) {
    case 'pending_parent_confirmation': return 'Awaiting Parent Acceptance';
    case 'confirmed':
    case 'scheduled': return past ? 'Awaiting Completion' : 'Confirmed';
    case 'awaiting_feedback': return 'Awaiting Feedback';
    case 'requested':
    case 'pending': return 'Requested';
    case 'pending_coordinator_action': return 'With Coordinator';
    case 'awaiting_reschedule': return 'Awaiting Reschedule';
    case 'waitlisted': return 'Unfulfilled';
    case 'rejected': return 'Declined';
    case 'cancelled': return 'Cancelled';
    case 'not_conducted': return 'Did Not Take Place';
    default: return (status || '').replace(/_/g, ' ');
  }
}

export function statusLabel(b, today = TODAY()) {
  const past = (b.assigned_date || '') < today;
  switch (b.status) {
    case 'pending_parent_confirmation':
      return past ? 'Awaiting your acceptance (date passed)' : 'Awaiting your acceptance';
    case 'requested':
    case 'pending':
      return 'Awaiting coordinator assignment';
    case 'pending_coordinator_action':
      return 'With coordinator';
    case 'awaiting_reschedule':
      return 'Awaiting a new time from your coordinator';
    case 'waitlisted':
      return 'With your school';
    case 'confirmed':
    case 'scheduled':
      return past ? 'Awaiting mentor completion' : 'Confirmed';
    case 'awaiting_feedback':
      return 'Completed — your feedback pending';
    case 'completed':
      return 'Completed';
    case 'cancelled':
      return 'Cancelled';
    case 'not_conducted':
      return 'Did not take place';
    case 'rejected':
      return 'Declined';
    default:
      return (b.status || '').replace(/_/g, ' ');
  }
}

export function statusTone(b) {
  return experienceDisplay(b).tone;
}

// ─── Lifecycle, in the school's own timezone ───────────────────────────────
// Statuses are never changed to suit the display; these are UI classifications.

export const SCHOOL_TZ = 'Asia/Kolkata';

/** "YYYY-MM-DDTHH:MM" for right now in the school's timezone, comparable as a string. */
export function nowInSchoolTz(tz = SCHOOL_TZ) {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', hour12: false,
  }).formatToParts(new Date());
  const get = t => parts.find(p => p.type === t)?.value || '00';
  const hour = get('hour') === '24' ? '00' : get('hour');
  return `${get('year')}-${get('month')}-${get('day')}T${hour}:${get('minute')}`;
}

const addMinutes = (hhmm, minutes) => {
  const [h, m] = hhmm.split(':').map(Number);
  const total = h * 60 + m + minutes;
  return `${String(Math.floor(total / 60) % 24).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`;
};

export function startsAt(b) {
  if (!b.assigned_date) return '';
  return `${b.assigned_date}T${(b.assigned_time_slot || '00:00').slice(0, 5)}`;
}

/** Scheduled end: the stored end time, or the start plus its duration. */
export function endsAt(b) {
  if (!b.assigned_date) return '';
  const start = (b.assigned_time_slot || '00:00').slice(0, 5);
  const end = (b.assigned_end_time || '').slice(0, 5)
    || addMinutes(start, b.assigned_duration_minutes || 90);
  if (end < start) {  // ran past midnight, so it ends early the following day
    const next = new Date(`${b.assigned_date}T00:00:00Z`);
    next.setUTCDate(next.getUTCDate() + 1);
    return `${next.toISOString().slice(0, 10)}T${end}`;
  }
  return `${b.assigned_date}T${end}`;
}

export const hasStarted = (b, now = nowInSchoolTz()) => !!startsAt(b) && startsAt(b) <= now;
export const hasEnded = (b, now = nowInSchoolTz()) => {
  const end = endsAt(b);
  return end ? end < now : (!!b.assigned_date && b.assigned_date < now.slice(0, 10));
};

const SCHEDULED = ['confirmed', 'scheduled', 'pending_parent_confirmation'];
const OPEN_WITH_COORDINATOR = ['requested', 'pending', 'pending_coordinator_action', 'waitlisted', 'awaiting_reschedule'];

/** Where an experience sits in its lifecycle. One value per experience. */
export function lifecycleState(b, now = nowInSchoolTz()) {
  if (b.admin_closed_at) return 'closed';
  const s = b.status;
  if (s === 'not_conducted') return 'not_conducted';
  if (['cancelled', 'rejected'].includes(s)) return 'cancelled';
  if (['completed', 'awaiting_feedback'].includes(s)) return 'completed';
  if (OPEN_WITH_COORDINATOR.includes(s)) return 'open';
  if (SCHEDULED.includes(s)) {
    if (hasEnded(b, now)) return 'awaiting_completion';
    if (hasStarted(b, now)) return 'in_progress';
    return 'upcoming';
  }
  return 'open';
}

/** The agreed status palette: yellow new, blue scheduled, amber overdue,
 *  green conducted, red not conducted, grey administratively closed. */
export const STATUS_TONES = {
  new_request: 'bg-yellow-50 text-yellow-800 hover:bg-yellow-50',
  unfulfilled: 'bg-orange-50 text-orange-700 hover:bg-orange-50',
  awaiting_parent: 'bg-violet-50 text-violet-700 hover:bg-violet-50',
  needs_slot: 'bg-violet-50 text-violet-700 hover:bg-violet-50',
  confirmed: 'bg-blue-50 text-blue-700 hover:bg-blue-50',
  awaiting_completion: 'bg-amber-100 text-amber-800 hover:bg-amber-100',
  awaiting_feedback: 'bg-green-50 text-green-700 hover:bg-green-50',
  completed: 'bg-green-50 text-green-700 hover:bg-green-50',
  not_conducted: 'bg-red-50 text-red-700 hover:bg-red-50',
  cancelled: 'bg-red-50 text-red-700 hover:bg-red-50',
  closed: 'bg-slate-100 text-slate-600 hover:bg-slate-100',
};

/** Matching dot colours for the calendar. */
export const STATUS_DOTS = {
  new_request: 'bg-yellow-500', unfulfilled: 'bg-orange-500',
  awaiting_parent: 'bg-violet-500', needs_slot: 'bg-violet-500',
  confirmed: 'bg-blue-500', awaiting_completion: 'bg-amber-500',
  awaiting_feedback: 'bg-green-500', completed: 'bg-green-500',
  not_conducted: 'bg-red-500', cancelled: 'bg-red-400', closed: 'bg-slate-400',
};

/** The shared palette, keyed by raw status for views that only know the status. */
export const STATUS_TONES_BY_STATUS = {
  requested: STATUS_TONES.new_request, pending: STATUS_TONES.new_request,
  pending_coordinator_action: STATUS_TONES.new_request,
  waitlisted: STATUS_TONES.unfulfilled,
  pending_parent_confirmation: STATUS_TONES.awaiting_parent,
  awaiting_reschedule: STATUS_TONES.needs_slot,
  confirmed: STATUS_TONES.confirmed, scheduled: STATUS_TONES.confirmed,
  awaiting_feedback: STATUS_TONES.awaiting_feedback, completed: STATUS_TONES.completed,
  cancelled: STATUS_TONES.cancelled, rejected: STATUS_TONES.cancelled,
  not_conducted: STATUS_TONES.not_conducted,
};

const DISPLAY_LABELS = {
  new_request: 'New request',
  unfulfilled: 'Unfulfilled',
  awaiting_parent: 'Awaiting parent',
  needs_slot: 'Needs new slot',
  confirmed: 'Confirmed',
  awaiting_completion: 'Awaiting completion',
  awaiting_feedback: 'Awaiting feedback',
  completed: 'Completed',
  not_conducted: 'Not conducted',
  cancelled: 'Cancelled',
  closed: 'Closed',
};

/** The badge an experience shows: { key, label, tone }. Display only. */
export function experienceDisplay(b, now = nowInSchoolTz()) {
  const life = lifecycleState(b, now);
  let key;
  if (life === 'closed') key = 'closed';
  else if (life === 'awaiting_completion') key = 'awaiting_completion';
  else if (life === 'not_conducted') key = 'not_conducted';
  else if (life === 'cancelled') key = 'cancelled';
  else if (life === 'completed') key = b.status === 'awaiting_feedback' ? 'awaiting_feedback' : 'completed';
  else if (b.status === 'waitlisted') key = 'unfulfilled';
  else if (b.status === 'awaiting_reschedule') key = 'needs_slot';
  else if (b.status === 'pending_parent_confirmation') key = 'awaiting_parent';
  else if (SCHEDULED.includes(b.status)) key = 'confirmed';
  else key = 'new_request';

  const label = key === 'cancelled' && b.status === 'rejected' ? 'Declined' : DISPLAY_LABELS[key];
  return { key, label, tone: STATUS_TONES[key] };
}

/** Today's date in the school's timezone, as "YYYY-MM-DD". */
export const todayInSchoolTz = (tz = SCHOOL_TZ) => nowInSchoolTz(tz).slice(0, 10);

/** A stored timestamp read as the school would read it, e.g. "Tue, 28 Oct". */
export function formatDayInSchoolTz(iso, tz = SCHOOL_TZ) {
  if (!iso) return '';
  const d = new Date(iso.length <= 10 ? `${iso}T12:00:00Z` : iso);
  if (Number.isNaN(d.getTime())) return '';
  return new Intl.DateTimeFormat('en-GB', {
    timeZone: tz, weekday: 'short', day: 'numeric', month: 'short',
  }).format(d);
}

/** Stored values have changed over time: workplace_visit today, offline/in-person historically. */export function isWorkplaceMode(mode) {
  const m = String(mode || '').toLowerCase();
  return m.includes('workplace') || m.includes('offline') || m.includes('person');
}

export function bookingMode(b) {
  const m = b.assigned_mode || b.requested_mode || b.mode || 'online';
  return isWorkplaceMode(m) ? 'Offline' : 'Online';
}

/** What a person reads for the experience type. */
export function modeLabelFor(mode) {
  return isWorkplaceMode(mode) ? 'Workplace Visit' : 'Online Session';
}

export function modeLabel(b) {
  return modeLabelFor(b.assigned_mode || b.requested_mode || b.mode || 'online');
}
